import { useApolloClient, useQuery } from "@apollo/client";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import {
  GET_ACTIVE_SCOFFLAWS,
  GetActiveScofflawsData,
  GetActiveScofflawsWhereExp,
  GetActiveScofflawsVars,
  GetActiveScofflawsWhereFilter,
  ActiveScofflawsOrderBy,
  EnforcementActiveScofflaw,
} from "common/graphql";
import {
  filterModelToSearchParams,
  parseFilterModelFromSearchParams,
  parseSortModel,
  serializeSortModel,
} from "common/graphql/queryString";
import { DataTableContainer } from "@app.automotus.io/components/DataTableContainer";
import Stack from "@mui/material/Stack";
import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useSearchParams } from "react-router-dom";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";

export const ActiveScofflawsTable: React.FC = () => {
  const client = useApolloClient();

  const TABLE_COLUMNS = React.useMemo(
    () => [
      {
        field: "licensePlate",
        headerName: "License Plate",
        align: "left",
        headerAlign: "left",
        valueGetter({ row }) {
          return `${row.registeredVehicle.stateCode} ${row.registeredVehicle.licensePlate}`;
        },
        sortable: false,
        filterOperators: getGridStringOperators().filter((op) => ["equals", "contains"].includes(op.value)),
        flex: 1,
      } as GridColDef<EnforcementActiveScofflaw, string>,
      {
        field: "numOutstandingIntents",
        headerName: "Outstanding Citations",
        align: "left",
        headerAlign: "left",
        sortable: true,
        filterable: false,
        type: "number",
        flex: 1,
      } as GridColDef<EnforcementActiveScofflaw, number>,
      {
        field: "scofflawBeginAt",
        headerName: "Scofflaw Since",
        align: "left",
        headerAlign: "left",
        valueGetter({ row }) {
          return new Date(row.scofflawBeginAt);
        },
        sortingOrder: ["desc", "asc"],
        filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
        flex: 1,
        type: "dateTime",
      } as GridColDef<EnforcementActiveScofflaw, Date>,
    ],
    [],
  );

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: "scofflawBeginAt+asc",
  });

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const PAGE_SIZE = 100;

  const sort = searchParams.get("sort") || "scofflawBeginAt+desc";
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const orderBy: ActiveScofflawsOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "scofflawBeginAt":
        return [{ scofflaw_begin_at: sortDirection || "asc" }];
      case "numOutstandingIntents":
        return [{ num_outstanding_intents: sortDirection || "asc" }];
      default:
        return [{ scofflaw_begin_at: sortDirection || "asc" }];
    }
  }, [sortModel]);

  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const filter: GetActiveScofflawsWhereFilter = React.useMemo(() => {
    const baseFilter: GetActiveScofflawsWhereFilter = {};

    if (!appliedFilters) {
      return baseFilter;
    }

    const columnFilters: GetActiveScofflawsWhereExp[] = appliedFilters.items
      .filter((item) => item.columnField === "licensePlate" || item.columnField === "scofflawBeginAt")
      .map((item) => {
        switch (item.columnField) {
          case "licensePlate": {
            return {
              registered_vehicle: {
                license_plate:
                  item.operatorValue === "equals" ? { _eq: item.value as string } : { _ilike: `%${item.value}%` },
              },
            };
          }
          default: {
            // We'll use scofflawBeginAt as the default case
            const filterDate = new Date(`${item.value}T00:00:00`);
            const dateStart = startOfDay(filterDate);
            const dateEnd = endOfDay(filterDate);

            switch (item.operatorValue) {
              case "is":
                return { scofflaw_begin_at: { _gte: dateStart, _lte: dateEnd } };
              case "before":
                return { scofflaw_begin_at: { _lt: dateStart } };
              case "after":
                return { scofflaw_begin_at: { _gt: dateEnd } };
              default:
                return { scofflaw_begin_at: { _eq: dateStart } };
            }
          }
        }
      });

    if (appliedFilters.linkOperator === "and") {
      return { ...baseFilter, _and: columnFilters };
    } else {
      return { ...baseFilter, _or: columnFilters };
    }
  }, [appliedFilters]);

  const { data, loading, error } = useQuery<GetActiveScofflawsData, GetActiveScofflawsVars>(GET_ACTIVE_SCOFFLAWS, {
    variables: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      orderBy,
      filter,
    },
    fetchPolicy: "network-only",
  });

  const fetchTotalCount = async () => {
    const result = await client.query<GetActiveScofflawsData, GetActiveScofflawsVars>({
      query: GET_ACTIVE_SCOFFLAWS,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
      },
    });

    return result.data?.activeScofflawAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetActiveScofflawsData, GetActiveScofflawsVars>({
      query: GET_ACTIVE_SCOFFLAWS,
      variables: {
        offset,
        limit,
        orderBy,
        filter,
      },
    });

    return result.data?.activeScofflaws || [];
  };

  return (
    <DataTableContainer>
      {({ apiRef, initialState }) => (
        <DataGridPro
          apiRef={apiRef}
          components={{
            Toolbar: TableToolbar,
            FilterPanel: ManualApplyGridFilterPanel,
          }}
          componentsProps={{
            filterPanel: {
              onApplyFilters: handleApplyFilters,
            },
            toolbar: {
              fetchTotalCount,
              fetchDataBatch,
              exportFileName: `vehicle_exemptions_${Date.now()}`,
              columns: TABLE_COLUMNS,
            },
          }}
          columns={TABLE_COLUMNS}
          getRowId={(row) => row.registeredVehicleId}
          rows={data?.activeScofflaws || []}
          rowCount={data?.activeScofflawAggregate.aggregate.count || 0}
          loading={loading}
          disableMultipleColumnsSorting
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableSelectionOnClick
          error={error}
          pagination
          page={page - 1}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
          paginationMode={"server"}
          rowsPerPageOptions={[PAGE_SIZE]}
          sortingMode={"server"}
          sortModel={sortModel}
          filterMode={"server"}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          initialState={initialState}
          onSortModelChange={handleSortModelChange}
          sx={{
            flexGrow: 1,
            ".MuiDataGrid-columnHeaderTitle": {
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "24px",
              letterSpacing: 0.17,
            },
          }}
        />
      )}
    </DataTableContainer>
  );
};

interface TableToolbarProps {
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
}

const TableToolbar: React.FC<TableToolbarProps> = ({ fetchTotalCount, fetchDataBatch, exportFileName, columns }) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" height={52} px={1.25} spacing={1}>
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
        <GridToolbarFilterButton />
        <CustomExportButton
          getTotalCount={fetchTotalCount}
          fetchDataBatch={fetchDataBatch}
          fileName={exportFileName}
          columns={columns}
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: 0.15,
            textTransform: "none",
          }}
        />
      </Stack>
    </Stack>
  );
};

export default ActiveScofflawsTable;
